/**
 * JiraPage — read-only Jira request-type catalog tab (Plan Jira-tab Task A4).
 *
 * Layout (top-to-bottom), each block in its own <TabErrorBoundary> with the
 * inline ErrorRetry card on `.isError`:
 *   1. KPI strip — 6 cards (Total / Curated / Discovered / Enriched % /
 *      Field-sync errors / Dead links). No deltas — the catalog is a static
 *      inventory, not a time-series.
 *   2. Search + quick-filter chips, then the request-types SortableTable.
 *      The dataset is small (a few dozen types), so search + chip filtering
 *      happens in-memory before the rows reach the table — no server round-trip.
 *      Each row expands (renderExpanded / expandedKeys / onToggleExpanded,
 *      mirroring BriefsPage) into a detail panel: when / not-for / examples /
 *      a fields table / source Confluence articles / the fields-sync error.
 *   3. Dead links — a Card listing article→requestType links whose target is
 *      NOT in the enriched-types table (mirrors the Slack `/sc-report jiras`
 *      dead-links framing).
 *
 * The catalog blocks (KPI strip / types table / dead links) ignore the global
 * FilterBar — the catalog is a static inventory, so `useJiraCatalog()` takes no
 * filters. The "Coverage gaps" section appended at the bottom (Task B3) is the
 * exception: those lists are trace-derived, so they DO respect the global
 * date-range + role filters via `useJiraGaps(filters)`. The section caption
 * makes that split explicit to the reader.
 */

import { RecountNotice } from '@/components/RecountNotice';
import * as React from 'react';

import {
  useJiraCatalog,
  useJiraGaps,
  useJiraTypeUsages,
  useJiraUsage,
} from '@/hooks/use-jira';
import { useUrlFilters } from '@/hooks/use-url-filters';
import {
  readTableSort,
  type Filters,
  type TableSort,
} from '@/lib/filters-schema';
import { formatRelativeCet } from '@/lib/format-date';
import { formatRating } from '@/lib/format';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { KPICard } from '@/components/KPICard';
import { SortableTable, type SortableColumn } from '@/components/SortableTable';
import { TabErrorBoundary } from '@/components/TabErrorBoundary';
import { Button } from '@/components/ui/button';
import { TraceDetailPanel } from '@/components/trace/TraceDetailPanel';
import { useFeedbackTraceDetail } from '@/hooks/use-feedback-trace-detail';
import type {
  JiraGapRow,
  JiraTypeRow,
  JiraUsageRow,
} from '@/services/api-types';

/**
 * Catalog row + the per-window usage counters merged in from `useJiraUsage`.
 * The merge happens BEFORE sorting so Drafts / Clicks are first-class sortable
 * columns rather than render-time decorations. Types with no traced usage in
 * the selected window default to 0/0.
 */
type JiraTypeRowWithUsage = JiraTypeRow & { drafts: number; clicks: number };

// ---------------------------------------------------------------------------
// Inline retry card — mirrors EngagementPage / BriefsPage ErrorRetry
// ---------------------------------------------------------------------------

function ErrorRetry({
  label,
  onRetry,
}: {
  label: string;
  onRetry: () => void;
}): React.ReactElement {
  return (
    <Card data-testid={`${label}-error`}>
      <CardContent className="p-4 space-y-2">
        <div className="text-sm font-medium text-red-600">
          {label} failed to load
        </div>
        <button
          type="button"
          onClick={onRetry}
          className="text-xs underline text-primary hover:no-underline"
        >
          Retry
        </button>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Small relative-time helper — delegates to the CET-aware SSOT. Returns
// em-dash for null so a "Never"-synced row doesn't print a broken string.
// ---------------------------------------------------------------------------

function relTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return formatRelativeCet(iso);
}

// ---------------------------------------------------------------------------
// Colored badge idioms — mirror EngagementPage's RoleBadge convention
// ---------------------------------------------------------------------------

/** Source: Curated (green) / Discovered (blue, with a 🆕). */
function SourceBadge({
  source,
}: {
  source: JiraTypeRow['source'];
}): React.ReactElement {
  if (source === 'curated') {
    return (
      <Badge className="bg-green-600 text-white hover:bg-green-600/80">
        Curated
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="bg-blue-100 text-blue-900">
      🆕 Discovered
    </Badge>
  );
}

/**
 * Retired: Jira no longer lists this request type, so nothing offers it to a salesperson
 * any more — not the search, not the request-type tools. The row is kept (the verdict
 * reverses itself the night Jira lists the type again), so this badge is the only place
 * the state is visible.
 */
function RetiredBadge({ deadSince }: { deadSince: string }): React.ReactElement {
  return (
    <Badge
      variant="destructive"
      className="bg-red-600 text-white mr-1 align-middle"
      title={`Jira stopped listing this request type on ${deadSince.slice(0, 10)} — it is kept here but never offered`}
    >
      🪦 Retired
    </Badge>
  );
}

/** Enrichment: Enriched (green ✓) / Not enriched (grey/secondary). */
function EnrichmentBadge({
  enriched,
}: {
  enriched: boolean;
}): React.ReactElement {
  if (enriched) {
    return (
      <Badge className="bg-green-600 text-white hover:bg-green-600/80">
        ✓ Enriched
      </Badge>
    );
  }
  return <Badge variant="secondary">Not enriched</Badge>;
}

/**
 * Fields-sync: OK (green) / Error (red — error text on hover via `title`) /
 * Never (grey). Appends the synced age when `fieldsSyncedAt` is present.
 */
function FieldsSyncBadge({
  row,
}: {
  row: JiraTypeRow;
}): React.ReactElement {
  const age =
    row.fieldsSyncedAt !== null ? ` · ${relTime(row.fieldsSyncedAt)}` : '';
  if (row.fieldsSyncStatus === 'ok') {
    return (
      <span className="inline-flex items-center gap-1">
        <Badge className="bg-green-600 text-white hover:bg-green-600/80">
          OK
        </Badge>
        {age && <span className="text-xs text-muted-foreground">{age}</span>}
      </span>
    );
  }
  if (row.fieldsSyncStatus === 'error') {
    return (
      <span
        className="inline-flex items-center gap-1"
        title={row.fieldsSyncError ?? ''}
      >
        <Badge variant="destructive" className="bg-red-600 text-white">
          Error
        </Badge>
        {age && <span className="text-xs text-muted-foreground">{age}</span>}
      </span>
    );
  }
  // 'never'
  return <Badge variant="secondary">Never</Badge>;
}

// ---------------------------------------------------------------------------
// Row-expansion detail panel (mirrors BriefsPage renderExpanded)
// ---------------------------------------------------------------------------

/** A labelled list block — renders the label + a bullet list, or nothing when
 *  the list is empty so the panel stays compact for sparse rows. */
function DetailList({
  label,
  items,
}: {
  label: string;
  items: string[];
}): React.ReactElement | null {
  if (items.length === 0) return null;
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <ul className="mt-1 list-disc pl-5 text-sm space-y-0.5">
        {items.map((it, i) => (
          <li key={i} className="whitespace-normal break-words">
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

function TypeRowDetail({
  row,
  filters,
}: {
  row: JiraTypeRow;
  filters: Filters;
}): React.ReactElement {
  return (
    // `min-w-0 max-w-full break-words`: the expanded cell uses `colSpan` over a
    // `w-max` table (see ui/table.tsx), so a long unbroken value inside this
    // panel would otherwise grow the table past the card and surface a
    // horizontal scrollbar. Pinning the panel to the available width + breaking
    // long words keeps everything wrapping within the row instead.
    <div className="min-w-0 max-w-full space-y-4 break-words">
      {/* When — wraps, em-dash when unset. */}
      <div>
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          When
        </div>
        <p className="mt-1 whitespace-normal break-words text-sm">
          {row.when ?? '—'}
        </p>
      </div>

      <DetailList label="Not for" items={row.notFor} />
      <DetailList label="Examples" items={row.examples} />

      {/* Fields table — name · type · required · allowed values. */}
      {row.fields.length > 0 && (
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Fields
          </div>
          <table className="mt-1 w-full max-w-full table-fixed text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th className="py-1 pr-3 font-medium">Name</th>
                <th className="py-1 pr-3 font-medium">Type</th>
                <th className="py-1 pr-3 font-medium">Required</th>
                <th className="py-1 font-medium">Allowed values</th>
              </tr>
            </thead>
            <tbody>
              {row.fields.map((f) => (
                <tr key={f.key} className="border-t border-border/50">
                  <td className="py-1 pr-3 align-top break-words">{f.name}</td>
                  <td className="py-1 pr-3 align-top break-words text-muted-foreground">
                    {f.fieldType}
                  </td>
                  <td className="py-1 pr-3 align-top">
                    {f.required ? 'Yes' : 'No'}
                  </td>
                  <td className="py-1 align-top break-words text-muted-foreground">
                    {f.allowedValues && f.allowedValues.length > 0
                      ? f.allowedValues.join(', ')
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Source Confluence articles — each a link, except orphaned links
          (url === '') which render as non-clickable plain text. */}
      {row.articles.length > 0 && (
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Source articles
          </div>
          <ul className="mt-1 space-y-1 text-sm">
            {row.articles.map((a) => (
              <li
                key={a.id}
                className="flex min-w-0 flex-wrap items-baseline gap-2"
              >
                {a.url ? (
                  <a
                    href={a.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="min-w-0 break-words underline text-primary hover:no-underline"
                  >
                    {a.title}
                  </a>
                ) : (
                  <span className="min-w-0 break-words">{a.title}</span>
                )}
                {a.lastModified && (
                  <span className="text-xs text-muted-foreground">
                    {relTime(a.lastModified)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Fields-sync error — only when the status is 'error'. */}
      {row.fieldsSyncStatus === 'error' && row.fieldsSyncError && (
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-red-600">
            Fields-sync error
          </div>
          <p className="mt-1 whitespace-normal break-words text-sm text-red-600">
            {row.fieldsSyncError}
          </p>
        </div>
      )}

      {/* Recent usages drill-down — lazy: this whole panel only mounts when the
          row is expanded, so the hook fires once per expansion (enabled). */}
      <TypeUsageList requestTypeId={row.requestTypeId} filters={filters} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quick-filter chip
// ---------------------------------------------------------------------------

function FilterChip({
  active,
  onToggle,
  children,
}: {
  active: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      className={
        active
          ? 'rounded-full border border-primary bg-primary px-3 py-1 text-xs font-medium text-primary-foreground'
          : 'rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground hover:text-foreground'
      }
    >
      {children}
    </button>
  );
}

const TYPES_TABLE_ID = 'jiraTypes';

/** Default sort — Drafts (per-window usage) desc, so the most-drafted types
 *  surface first. Kept in lock-step with the `defaultSort` prop on the
 *  <SortableTable> so the header arrow and the initial row order agree, and
 *  applied locally as the fallback when the URL carries no sort for this
 *  table (see `readTableSort(...) ?? TYPES_DEFAULT_SORT` below). */
const TYPES_DEFAULT_SORT: TableSort = { column: 'drafts', dir: 'desc' };

/** Every meaningful column is sortable. `SortableTable` only writes the chosen
 *  sort to the URL — it does NOT reorder its `rows` prop — so the page must
 *  sort the filtered rows itself before handing them over. Anything outside
 *  this set (e.g. a hand-edited URL pointing at a non-sortable column) leaves
 *  the filtered order untouched. */
const TYPES_SORTABLE_KEYS: ReadonlySet<string> = new Set([
  'requestTypeId',
  'requestTypeName',
  'serviceDeskId',
  'source',
  'enriched',
  'fieldsSyncStatus',
  'articleCount',
  'drafts',
  'clicks',
  'createdAt',
  'updatedAt',
]);

/**
 * Client-side comparator for the request-types table. Mirrors BriefsPage's
 * `sortBriefLogRows`: read the URL sort, apply a column-aware comparator,
 * then hand the ordered rows to the table. Operates on the usage-merged
 * view-model so the Drafts / Clicks numeric columns sort alongside the static
 * catalog fields.
 *
 * Per-column behavior:
 *   - `requestTypeId`    : numeric (the drafted-request id, primary search key).
 *   - `requestTypeName`  : case-insensitive string `localeCompare`.
 *   - `serviceDeskId`    : numeric.
 *   - `source`           : string compare on the literal union
 *     ('curated' < 'discovered' alphabetically).
 *   - `enriched`         : boolean (false < true) numerically.
 *   - `fieldsSyncStatus` : string compare on the literal union ('error' <
 *     'never' < 'ok' alphabetically) — the union ordering is fine here.
 *   - `articleCount` / `drafts` / `clicks` : numeric.
 *   - `createdAt` / `updatedAt` : ISO timestamp via `Date.getTime()`.
 *
 * Ties break on `requestTypeId` (asc) so equal keys keep a stable order across
 * renders instead of jiggling. Columns outside `TYPES_SORTABLE_KEYS` return
 * the rows unchanged (the backend's fixed order).
 */
function sortTypeRows(
  rows: JiraTypeRowWithUsage[],
  sort: TableSort,
): JiraTypeRowWithUsage[] {
  if (!TYPES_SORTABLE_KEYS.has(sort.column)) return rows;
  const dir = sort.dir === 'asc' ? 1 : -1;
  return rows.slice().sort((a, b) => {
    let primary = 0;
    if (sort.column === 'requestTypeId') {
      primary = (a.requestTypeId - b.requestTypeId) * dir;
    } else if (sort.column === 'requestTypeName') {
      primary = a.requestTypeName.localeCompare(b.requestTypeName) * dir;
    } else if (sort.column === 'serviceDeskId') {
      primary = (a.serviceDeskId - b.serviceDeskId) * dir;
    } else if (sort.column === 'source') {
      primary = a.source.localeCompare(b.source) * dir;
    } else if (sort.column === 'enriched') {
      primary = (Number(a.enriched) - Number(b.enriched)) * dir;
    } else if (sort.column === 'fieldsSyncStatus') {
      primary = a.fieldsSyncStatus.localeCompare(b.fieldsSyncStatus) * dir;
    } else if (sort.column === 'articleCount') {
      primary = (a.articleCount - b.articleCount) * dir;
    } else if (sort.column === 'drafts') {
      primary = (a.drafts - b.drafts) * dir;
    } else if (sort.column === 'clicks') {
      primary = (a.clicks - b.clicks) * dir;
    } else if (sort.column === 'createdAt') {
      primary =
        (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) *
        dir;
    } else if (sort.column === 'updatedAt') {
      primary =
        (new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()) *
        dir;
    }
    if (primary !== 0) return primary;
    // Stable tiebreak so equal keys don't reshuffle between renders.
    return a.requestTypeId - b.requestTypeId;
  });
}

// ---------------------------------------------------------------------------
// Coverage-gaps tables (Task B3)
// ---------------------------------------------------------------------------

const LOW_RATED_TABLE_ID = 'jiraLowRated';
const NO_MATCH_TABLE_ID = 'jiraNoMatch';

/** Client-side page size for the low-rated drafts table. The list is capped at
 *  200 rows server-side, so a 15-row page keeps the panel scannable without
 *  rendering the whole list at once. */
const LOW_RATED_PAGE_SIZE = 15;

/**
 * Free-text cell. Default (the coverage-gaps tables): truncate to a fixed
 * width with the full value on hover, so a single row can't blow out the
 * table. `wrap` (the expanded drill-down): wrap and break long unbroken
 * strings instead — the panel has no fixed column width, so truncation there
 * would just clip text rather than fit a layout. `wrap` also constrains to the
 * parent width so a long value never forces a horizontal scrollbar.
 */
function TextCell({
  text,
  wrap = false,
}: {
  text: string;
  wrap?: boolean;
}): React.ReactElement {
  const value = text || '—';
  if (wrap) {
    return (
      <span className="block max-w-full whitespace-normal break-words">
        {value}
      </span>
    );
  }
  return (
    <span className="block max-w-[360px] truncate" title={value}>
      {value}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Per-type usage drill-down (rendered inside the expanded catalog row)
// ---------------------------------------------------------------------------

/**
 * Forward-only empty-state copy for the per-type drill-down. An empty list does
 * NOT mean the type was never drafted — draft→type attribution is forward-only,
 * it only began landing when the usage-tracking change shipped. Keep it short.
 */
const USAGE_EMPTY_COPY =
  'No usages yet — draft attribution started when the usage-tracking change ' +
  'shipped.';

/**
 * Recent draft usages for one request type. Lazy by construction: the whole
 * expanded panel only mounts when its row is open, so calling the hook with
 * `enabled=true` here means the endpoint is hit once per expansion — never for
 * a collapsed row. `filters` is threaded down so the drill-down honours the
 * same date-range + role window as the rest of the trace-derived blocks.
 */
function TypeUsageList({
  requestTypeId,
  filters,
}: {
  requestTypeId: number;
  filters: Filters;
}): React.ReactElement {
  const usages = useJiraTypeUsages(requestTypeId, filters, true);
  const rows: JiraUsageRow[] = usages.data ?? [];

  return (
    <div className="min-w-0 max-w-full">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Recent usages
      </div>

      {usages.isLoading ? (
        <p className="mt-1 text-sm text-muted-foreground">Loading usages…</p>
      ) : usages.isError ? (
        <p className="mt-1 text-sm text-red-600">
          Couldn’t load usages.{' '}
          <button
            type="button"
            onClick={() => void usages.refetch()}
            className="underline hover:no-underline"
          >
            Retry
          </button>
        </p>
      ) : rows.length === 0 ? (
        <p className="mt-1 whitespace-normal break-words text-sm text-muted-foreground">
          {USAGE_EMPTY_COPY}
        </p>
      ) : (
        <table className="mt-1 w-full max-w-full table-fixed text-sm">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              <th className="w-[12%] py-1 pr-3 font-medium">User</th>
              <th className="w-[12%] py-1 pr-3 font-medium">When</th>
              <th className="w-[8%] py-1 pr-3 font-medium">Rating</th>
              <th className="w-[24%] py-1 pr-3 font-medium">Ask</th>
              <th className="w-[24%] py-1 pr-3 font-medium">Reply</th>
              <th className="w-[20%] py-1 font-medium">Feedback</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.traceId} className="border-t border-border/50">
                <td className="py-1 pr-3 align-top break-words">
                  {u.userId ?? '—'}
                </td>
                <td className="py-1 pr-3 align-top text-muted-foreground">
                  {relTime(u.timestamp)}
                </td>
                <td className="py-1 pr-3 align-top">
                  {formatRating(u.rating)}
                </td>
                <td className="py-1 pr-3 align-top">
                  <TextCell text={u.input} wrap />
                </td>
                <td className="py-1 pr-3 align-top">
                  <TextCell text={u.outputSnippet} wrap />
                </td>
                <td className="py-1 align-top text-muted-foreground">
                  <TextCell text={u.textFeedback ?? ''} wrap />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * Low-rated drafts (GAP-2): User · When · Rating · Ask · Reply.
 *
 * Column widths are load-bearing: the table renders with `table-fixed` (via the
 * `tableClassName` prop at the call site) so the wide expanded colSpan sub-row
 * can't reflow the columns or force a horizontal scrollbar on expand. The
 * identifier/meta columns take fixed widths; Ask + Reply each take a fraction so
 * they share the remaining space and wrap inside the locked layout.
 */
const LOW_RATED_COLUMNS: Array<SortableColumn<JiraGapRow>> = [
  {
    key: 'userId',
    header: 'User',
    width: 'w-32',
    format: (_v, row) => row.userId ?? '—',
  },
  {
    key: 'timestamp',
    header: 'When',
    width: 'w-28',
    format: (v) => relTime(v as string),
  },
  {
    key: 'rating',
    header: 'Rating',
    align: 'right',
    width: 'w-20',
    format: (_v, row) => formatRating(row.rating),
  },
  {
    key: 'input',
    header: 'Ask',
    width: 'w-[35%]',
    format: (_v, row) => <TextCell text={row.input} />,
  },
  {
    key: 'outputSnippet',
    header: 'Reply',
    width: 'w-[35%]',
    format: (_v, row) => <TextCell text={row.outputSnippet} />,
  },
];

/**
 * Shared expanded detail for a coverage-gap row — used by BOTH gap tables
 * (low-rated drafts AND no-match asks), since both are `JiraGapRow` keyed by
 * `traceId`. Reuses the SAME canonical trace-detail renderer as the Feedback
 * tab (TraceDetailPanel + lazy useFeedbackTraceDetail fetch), mirroring
 * FeedbackTable's RowExpandPanel: no bespoke formatting — full query/answer
 * with the shared wrapping + metadata layout, revealing the FULL ask + reply
 * that the collapsed Ask/Reply cells truncate (via `TextCell`).
 *
 * The Feedback subsection only renders when `rating`/`textFeedback` is present
 * (TraceDetailPanel self-hides it otherwise), so it shows the WHY for a
 * low-rated draft (rating + free-text) and quietly drops out for a no-match
 * ask (both null). The row's input/outputSnippet are passed as previews so
 * something shows while the untruncated Langfuse fetch is in flight (or if the
 * trace rotated out of retention).
 */
function GapTraceDetail({ row }: { row: JiraGapRow }): React.ReactElement {
  const detail = useFeedbackTraceDetail(row.traceId, true);

  if (detail.isLoading) {
    return (
      <div className="space-y-2" data-testid="gap-expand-loading">
        <div className="h-3 w-24 rounded bg-muted animate-pulse" />
        <div className="h-3 w-full rounded bg-muted animate-pulse" />
        <div className="h-3 w-3/4 rounded bg-muted animate-pulse" />
      </div>
    );
  }

  if (detail.isError) {
    return (
      <div className="space-y-2 text-sm" data-testid="gap-expand-error">
        <div className="font-medium text-red-600">Failed to load trace detail.</div>
        <div className="text-xs text-muted-foreground">
          The Langfuse fetch did not return — the trace may have rotated out of
          retention, or the upstream is unreachable.
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void detail.refetch()}
        >
          Retry
        </Button>
      </div>
    );
  }

  const data = detail.data;
  return (
    <TraceDetailPanel
      variant="inline"
      detail={{
        traceId: row.traceId,
        timestamp: row.timestamp,
        durationMs: null,
        cost: null,
        input: data?.input ?? row.input ?? null,
        output: data?.output ?? row.outputSnippet ?? null,
        // …and what the code drew beside that answer, when the fetch carried any.
        codeDrawn: data?.codeDrawn ?? null,
        error: data?.error ?? null,
      }}
      feedback={{ rating: row.rating, text: row.textFeedback }}
    />
  );
}

/**
 * No-match asks (GAP-1): User · When · Ask · Top score · Reply.
 *
 * Column widths are load-bearing — same reasoning as LOW_RATED_COLUMNS: the
 * table renders with `table-fixed` (via the `tableClassName` prop at the call
 * site) so the wide expanded colSpan sub-row can't reflow the columns or force
 * a horizontal scrollbar on expand. Identifier/meta columns take fixed widths;
 * Ask + Reply each take a fraction so they share the remaining space and wrap
 * inside the locked layout.
 */
const NO_MATCH_COLUMNS: Array<SortableColumn<JiraGapRow>> = [
  {
    key: 'userId',
    header: 'User',
    width: 'w-32',
    format: (_v, row) => row.userId ?? '—',
  },
  {
    key: 'timestamp',
    header: 'When',
    width: 'w-28',
    format: (v) => relTime(v as string),
  },
  {
    key: 'input',
    header: 'Ask',
    width: 'w-[35%]',
    format: (_v, row) => <TextCell text={row.input} />,
  },
  {
    key: 'topScore',
    header: 'Top score',
    align: 'right',
    width: 'w-24',
    format: (_v, row) => (row.topScore !== null ? row.topScore.toFixed(2) : '—'),
  },
  {
    key: 'outputSnippet',
    header: 'Reply',
    width: 'w-[35%]',
    format: (_v, row) => <TextCell text={row.outputSnippet} />,
  },
];

/**
 * Forward-only empty-state copy for GAP-1. An empty no-match list does NOT
 * mean "no gaps" — the underlying trace metadata only began landing when the
 * trace-observability change shipped, so an empty list early on just means we
 * haven't collected enough data yet. `SortableTable.emptyMessage` is a plain
 * string, so this multi-sentence note is rendered above the table instead.
 */
const NO_MATCH_EMPTY_COPY =
  'Collecting since the trace-observability change shipped — an empty list ' +
  'here means no data yet, not no gaps.';

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function JiraPage(): React.ReactElement {
  const { filters } = useUrlFilters();
  const catalog = useJiraCatalog();
  // Coverage gaps are trace-derived → filter-aware (date-range + role), unlike
  // the static catalog above.
  const gaps = useJiraGaps(filters);
  // Per-type usage counters (drafts/clicks) — also trace-derived, so
  // filter-aware. Merged onto the catalog rows below so the Drafts/Clicks
  // columns sort alongside the static catalog fields.
  const usage = useJiraUsage(filters);
  const data = catalog.data;
  const kpis = data?.kpis;
  const types = data?.types ?? [];
  const deadLinks = data?.deadLinks ?? [];
  const lowRatedDrafts = gaps.data?.lowRatedDrafts ?? [];
  const noMatch = gaps.data?.noMatch ?? [];

  // Inline-expand state — a Set of stringified requestTypeIds (matching
  // getRowKey below). Mirrors BriefsPage's expandedKeys wiring, but kept in
  // local component state rather than the URL since the catalog has no other
  // URL-synced state to coordinate with.
  const [expandedKeys, setExpandedKeys] = React.useState<Set<string>>(
    () => new Set(),
  );
  const onToggleExpanded = React.useCallback((row: JiraTypeRow) => {
    const key = String(row.requestTypeId);
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const renderExpanded = React.useCallback(
    (row: JiraTypeRow): React.ReactNode => (
      <TypeRowDetail row={row} filters={filters} />
    ),
    [filters],
  );

  // Low-rated drafts expand state — a SEPARATE Set keyed by traceId so it never
  // collides with the catalog table's `expandedKeys` (keyed by requestTypeId).
  const [lowRatedExpanded, setLowRatedExpanded] = React.useState<Set<string>>(
    () => new Set(),
  );
  const onToggleLowRated = React.useCallback((row: JiraGapRow) => {
    setLowRatedExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(row.traceId)) next.delete(row.traceId);
      else next.add(row.traceId);
      return next;
    });
  }, []);
  const renderLowRatedExpanded = React.useCallback(
    (row: JiraGapRow): React.ReactNode => <GapTraceDetail row={row} />,
    [],
  );

  // No-match expand state — a SEPARATE Set keyed by traceId (mirrors the
  // low-rated wiring above) so it never collides with the low-rated table's
  // `lowRatedExpanded` or the catalog table's `expandedKeys`. Both gap tables
  // share the SAME GapTraceDetail renderer — only the expand-state Set differs.
  const [noMatchExpanded, setNoMatchExpanded] = React.useState<Set<string>>(
    () => new Set(),
  );
  const onToggleNoMatch = React.useCallback((row: JiraGapRow) => {
    setNoMatchExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(row.traceId)) next.delete(row.traceId);
      else next.add(row.traceId);
      return next;
    });
  }, []);
  const renderNoMatchExpanded = React.useCallback(
    (row: JiraGapRow): React.ReactNode => <GapTraceDetail row={row} />,
    [],
  );

  // Client-side pagination for the low-rated drafts table. The list is small
  // (≤200 rows server-side), so paging happens in-memory after the rows land.
  const [lowRatedPage, setLowRatedPage] = React.useState(0);

  // Search + quick filters — applied in-memory before the rows reach the
  // table. Active chips combine with AND.
  const [query, setQuery] = React.useState('');
  const [showSyncErrors, setShowSyncErrors] = React.useState(false);
  const [showNotEnriched, setShowNotEnriched] = React.useState(false);
  const [showDiscovered, setShowDiscovered] = React.useState(false);

  // Per-type usage lookup — requestTypeId → {drafts, clicks}. Rebuilt only when
  // the usage response changes; types absent from the map default to 0/0 in
  // the merge below.
  const usageById = React.useMemo(() => {
    const m = new Map<number, { drafts: number; clicks: number }>();
    for (const u of usage.data ?? []) {
      m.set(u.requestTypeId, { drafts: u.drafts, clicks: u.clicks });
    }
    return m;
  }, [usage.data]);

  const filteredTypes = React.useMemo<JiraTypeRowWithUsage[]>(() => {
    const q = query.trim().toLowerCase();
    return types
      .filter((r) => {
        // Match the query against EITHER the type name (case-insensitive) OR
        // the request-type id, so "1329" finds the type whose id is 1329 while
        // text still matches the name. The id is the primary thing users search
        // by, and a partial-numeric query (e.g. "13") matches any id containing
        // those digits.
        if (
          q &&
          !r.requestTypeName.toLowerCase().includes(q) &&
          !String(r.requestTypeId).includes(q)
        )
          return false;
        if (showSyncErrors && r.fieldsSyncStatus !== 'error') return false;
        if (showNotEnriched && r.enriched) return false;
        if (showDiscovered && r.source !== 'discovered') return false;
        return true;
      })
      // Merge usage BEFORE sorting so Drafts/Clicks are sortable columns.
      // Types with no usage in the window get 0/0.
      .map((r) => {
        const u = usageById.get(r.requestTypeId);
        return { ...r, drafts: u?.drafts ?? 0, clicks: u?.clicks ?? 0 };
      });
  }, [
    types,
    usageById,
    query,
    showSyncErrors,
    showNotEnriched,
    showDiscovered,
  ]);

  // Sort the already-filtered rows BEFORE they reach the table: SortableTable
  // only mirrors the chosen sort to the URL, it doesn't reorder its `rows`.
  // Read the URL sort for this table (falling back to the Drafts-desc default)
  // and apply the comparator so every sortable header actually reorders.
  const sort = readTableSort(filters, TYPES_TABLE_ID) ?? TYPES_DEFAULT_SORT;
  const sortedTypes = React.useMemo(
    () => sortTypeRows(filteredTypes, sort),
    [filteredTypes, sort],
  );

  // Low-rated pagination derivations. `pageCount` is at least 1 so an empty
  // list still renders page "1 of 1" math without a divide-by-zero, and the
  // controls (hidden below ≤1 page) stay off.
  const lowRatedCount = lowRatedDrafts.length;
  const lowRatedPageCount = Math.max(
    1,
    Math.ceil(lowRatedCount / LOW_RATED_PAGE_SIZE),
  );
  // Reset to page 0 whenever the filters change OR the row count shrinks so the
  // current page would start past the end — otherwise a user who paged to the
  // back of a long list and then narrowed the date range lands on a blank page.
  React.useEffect(() => {
    setLowRatedPage((p) =>
      p > 0 && p * LOW_RATED_PAGE_SIZE >= lowRatedCount ? 0 : p,
    );
  }, [lowRatedCount, filters]);
  const lowRatedStart = lowRatedPage * LOW_RATED_PAGE_SIZE;
  const lowRatedPageRows = lowRatedDrafts.slice(
    lowRatedStart,
    lowRatedStart + LOW_RATED_PAGE_SIZE,
  );

  // Column widths are load-bearing here: the catalog table renders with
  // `table-fixed` (via the `tableClassName` prop below) so column widths come
  // from these header cells, not from cell content. Fixing the widths is what
  // stops the full-width expanded sub-row from reflowing the columns on expand.
  const columns: Array<SortableColumn<JiraTypeRowWithUsage>> = React.useMemo(
    () => [
      {
        // The drafted-request id — the primary value users search by, so it
        // leads the table. Numeric → right-aligned, sorts numerically.
        key: 'requestTypeId',
        header: 'ID',
        sortable: true,
        align: 'right',
        width: 'w-20',
      },
      {
        key: 'requestTypeName',
        header: 'Name',
        sortable: true,
        width: 'w-72',
        format: (_v, row) => (
          <span className="break-words">
            {row.deadSince && <RetiredBadge deadSince={row.deadSince} />}
            {row.createUrl ? (
              <a
                href={row.createUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-primary hover:no-underline break-words"
              >
                {row.requestTypeName}
              </a>
            ) : (
              row.requestTypeName
            )}
          </span>
        ),
      },
      {
        key: 'serviceDeskId',
        header: 'Service Desk',
        sortable: true,
        align: 'right',
        width: 'w-28',
      },
      {
        key: 'source',
        header: 'Source',
        sortable: true,
        width: 'w-28',
        format: (_v, row) => <SourceBadge source={row.source} />,
      },
      {
        key: 'enriched',
        header: 'Enrichment',
        sortable: true,
        width: 'w-28',
        format: (_v, row) => <EnrichmentBadge enriched={row.enriched} />,
      },
      {
        key: 'fieldsSyncStatus',
        header: 'Fields sync',
        sortable: true,
        width: 'w-28',
        format: (_v, row) => <FieldsSyncBadge row={row} />,
      },
      {
        key: 'articleCount',
        header: 'Articles',
        sortable: true,
        align: 'right',
        width: 'w-24',
      },
      {
        key: 'drafts',
        header: 'Drafts',
        sortable: true,
        align: 'right',
        width: 'w-24',
      },
      {
        key: 'clicks',
        header: 'Clicks',
        sortable: true,
        align: 'right',
        width: 'w-24',
      },
      {
        key: 'createdAt',
        header: 'Added',
        sortable: true,
        align: 'right',
        width: 'w-28',
        format: (v) => relTime(v as string),
      },
      {
        key: 'updatedAt',
        header: 'Updated',
        sortable: true,
        align: 'right',
        width: 'w-28',
        format: (v) => relTime(v as string),
      },
    ],
    [],
  );

  return (
    <section aria-labelledby="jira-heading" className="space-y-6">
      <header>
        <h1 id="jira-heading" className="text-2xl font-semibold">
          Jira
        </h1>
        <p className="text-sm text-muted-foreground">
          Request-type catalog — curated vs auto-discovered types, KB
          enrichment, field-sync health, and dead article links.
        </p>
        <RecountNotice queries={[gaps, usage]} />
      </header>

      {/* 1 — KPI strip (no deltas). 6 cards. */}
      <TabErrorBoundary name="KPI row">
        {catalog.isError ? (
          <ErrorRetry label="KPIs" onRetry={() => void catalog.refetch()} />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 md:gap-4">
            <KPICard
              label="Total"
              value={kpis?.total ?? null}
              loading={catalog.isLoading}
              showDelta={false}
              tooltip="Total request types in the catalog (curated + auto-discovered)."
            />
            <KPICard
              label="Curated"
              value={kpis?.curated ?? null}
              loading={catalog.isLoading}
              showDelta={false}
              tooltip="Request types hand-curated in config/jira-request-types.yaml."
            />
            <KPICard
              label="Discovered"
              value={kpis?.discovered ?? null}
              loading={catalog.isLoading}
              showDelta={false}
              tooltip="Request types auto-discovered from Jira but not yet curated."
            />
            <KPICard
              label="Enriched %"
              // `enrichedPct` is already 0..100 (backend: round(enriched/total*100));
              // KPICard `percent` only appends "%" without scaling — pass it as-is.
              value={kpis === undefined ? null : kpis.enrichedPct}
              format="percent"
              loading={catalog.isLoading}
              showDelta={false}
              subLabel={
                kpis ? `${kpis.enriched} of ${kpis.total} enriched` : undefined
              }
              tooltip="Share of request types with at least one linked KB article."
            />
            <KPICard
              label="Field-sync errors"
              value={kpis?.fieldsSyncErrors ?? null}
              loading={catalog.isLoading}
              showDelta={false}
              subLabel={
                kpis ? `${kpis.neverSynced} never synced` : undefined
              }
              tooltip="Request types whose Jira create-form field schema failed to sync."
            />
            <KPICard
              label="Dead links"
              value={kpis?.deadLinks ?? null}
              loading={catalog.isLoading}
              showDelta={false}
              tooltip="KB articles pointing at a request type that isn't in the enriched-types table."
            />
          </div>
        )}
      </TabErrorBoundary>

      {/* 2 — Search + quick filters + request-types table. */}
      <TabErrorBoundary name="Request types">
        {catalog.isError ? (
          <ErrorRetry
            label="Request types"
            onRetry={() => void catalog.refetch()}
          />
        ) : (
          <div className="space-y-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm font-medium text-muted-foreground">
                Request types
              </div>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name or ID…"
                aria-label="Search request types by name or ID"
                className="h-9 w-full rounded-md border border-border bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:w-64"
              />
            </div>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Quick filters">
              <FilterChip
                active={showSyncErrors}
                onToggle={() => setShowSyncErrors((v) => !v)}
              >
                Field-sync errors
              </FilterChip>
              <FilterChip
                active={showNotEnriched}
                onToggle={() => setShowNotEnriched((v) => !v)}
              >
                Not enriched
              </FilterChip>
              <FilterChip
                active={showDiscovered}
                onToggle={() => setShowDiscovered((v) => !v)}
              >
                Discovered
              </FilterChip>
            </div>
            {/* The catalog inventory itself is global, but the per-type usage
                columns are trace-derived → window/role-scoped. Flag the split
                so a reader doesn't mistake the counts for all-time totals. */}
            <p className="text-xs text-muted-foreground">
              Drafts and Clicks reflect the selected date range and role above;
              the catalog inventory itself is global.
            </p>
            <SortableTable<JiraTypeRowWithUsage>
              tableId={TYPES_TABLE_ID}
              columns={columns}
              rows={sortedTypes}
              // Fixed layout: column widths come from the header cells (set via
              // each column's `width`), so the full-width expanded colSpan
              // sub-row can never reflow them (the column-shift-on-expand bug).
              tableClassName="table-fixed w-full"
              getRowKey={(r) => String(r.requestTypeId)}
              defaultSort={TYPES_DEFAULT_SORT}
              /* BOTH queries, not just the catalog. Drafts and Clicks come
                 from `usage`, which is window/role-scoped, while `catalog` is
                 global and does NOT refetch when the filters change — so a
                 filter change left every row printing `0 / 0` under a caption
                 promising window-scoped counts, and the table's default sort
                 is by one of those columns. */
              isLoading={catalog.isLoading || usage.isLoading}
              emptyMessage={
                !catalog.isLoading && types.length === 0
                  ? 'No Jira request types synced yet.'
                  : 'No request types match the current filters.'
              }
              stickyHeader
              maxHeight="max-h-[520px]"
              renderExpanded={renderExpanded}
              expandedKeys={expandedKeys}
              onToggleExpanded={onToggleExpanded}
            />
          </div>
        )}
      </TabErrorBoundary>

      {/* 3 — Dead links (mirrors the Slack `/sc-report jiras` dead-links block). */}
      <TabErrorBoundary name="Dead links">
        {catalog.isError ? (
          <ErrorRetry label="Dead links" onRetry={() => void catalog.refetch()} />
        ) : (
          <Card>
            <CardHeader className="p-4 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Dead links ({deadLinks.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              {deadLinks.length === 0 ? (
                <p className="text-sm text-muted-foreground">No dead links.</p>
              ) : (
                <ul className="space-y-3 text-sm">
                  {deadLinks.map((dl) => (
                    <li
                      key={dl.requestTypeId}
                      className="space-y-1 border-b border-border/50 pb-3 last:border-0 last:pb-0"
                    >
                      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        {dl.createUrl ? (
                          <a
                            href={dl.createUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium underline text-primary hover:no-underline"
                          >
                            Type {dl.requestTypeId}
                          </a>
                        ) : (
                          <span className="font-medium">
                            Type {dl.requestTypeId}
                          </span>
                        )}
                        <span className="text-xs text-muted-foreground">
                          Service Desk {dl.serviceDeskId}
                        </span>
                      </div>
                      {dl.articles.length > 0 && (
                        <ul className="list-disc pl-5 text-xs space-y-0.5">
                          {dl.articles.map((a) => (
                            <li key={a.id}>
                              {a.url ? (
                                <a
                                  href={a.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="underline text-primary hover:no-underline"
                                >
                                  {a.title}
                                </a>
                              ) : (
                                <span>{a.title}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        )}
      </TabErrorBoundary>

      {/* 4 — Coverage gaps (Task B3). UNLIKE the catalog blocks above, these
          lists are trace-derived and respect the global FilterBar. */}
      <TabErrorBoundary name="Coverage gaps">
        {gaps.isError ? (
          <ErrorRetry
            label="Coverage gaps"
            onRetry={() => void gaps.refetch()}
          />
        ) : (
          <div className="space-y-4">
            <div>
              <h2 className="text-sm font-medium text-muted-foreground">
                Coverage gaps
              </h2>
              <p className="text-xs text-muted-foreground">
                Filtered by the date range and role above.
              </p>
            </div>

            {/* Low-rated drafts (GAP-2). Each row expands to the full ask +
                reply text (collapsed cells truncate via TextCell); the list is
                paged client-side at LOW_RATED_PAGE_SIZE rows. */}
            <div className="space-y-2">
              <div className="text-sm font-medium">Low-rated drafts</div>
              <SortableTable<JiraGapRow>
                tableId={LOW_RATED_TABLE_ID}
                columns={LOW_RATED_COLUMNS}
                rows={lowRatedPageRows}
                getRowKey={(r) => r.traceId}
                isLoading={gaps.isLoading}
                emptyMessage="No low-rated drafts in range"
                stickyHeader
                // `table-fixed w-full` locks column widths to the header cells
                // (see LOW_RATED_COLUMNS widths) so the wide expanded colSpan
                // sub-row wraps inside the locked layout instead of growing the
                // table and triggering a horizontal scrollbar on expand.
                tableClassName="table-fixed w-full"
                renderExpanded={renderLowRatedExpanded}
                expandedKeys={lowRatedExpanded}
                onToggleExpanded={onToggleLowRated}
              />
              {/* Pagination controls — hidden when the whole list fits on one
                  page (≤1 page) so a short list shows no chrome. */}
              {!gaps.isLoading && lowRatedPageCount > 1 && (
                <div
                  className="flex items-center justify-between gap-2 text-xs text-muted-foreground"
                  data-testid="low-rated-pagination"
                >
                  <span data-testid="low-rated-page-indicator">
                    Page {lowRatedPage + 1} of {lowRatedPageCount} · showing{' '}
                    {lowRatedStart + 1}–
                    {Math.min(lowRatedStart + LOW_RATED_PAGE_SIZE, lowRatedCount)}{' '}
                    of {lowRatedCount}
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      data-testid="low-rated-prev"
                      onClick={() =>
                        setLowRatedPage((p) => Math.max(0, p - 1))
                      }
                      disabled={lowRatedPage === 0}
                      className="rounded-md border border-border bg-card px-2 py-1 font-medium hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Prev
                    </button>
                    <button
                      type="button"
                      data-testid="low-rated-next"
                      onClick={() =>
                        setLowRatedPage((p) =>
                          Math.min(lowRatedPageCount - 1, p + 1),
                        )
                      }
                      disabled={lowRatedPage >= lowRatedPageCount - 1}
                      className="rounded-md border border-border bg-card px-2 py-1 font-medium hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Asked but no matching type (GAP-1). Each row expands to the full
                ask + reply text (collapsed cells truncate via TextCell), using
                the SAME canonical GapTraceDetail panel as the low-rated table.
                The forward-only empty-state copy renders above the table when
                the list is empty, since SortableTable's emptyMessage is a plain
                string and this note needs more than one sentence. */}
            <div className="space-y-2">
              <div className="text-sm font-medium">
                Asked but no matching type
              </div>
              {!gaps.isLoading && noMatch.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  {NO_MATCH_EMPTY_COPY}
                </p>
              )}
              <SortableTable<JiraGapRow>
                tableId={NO_MATCH_TABLE_ID}
                columns={NO_MATCH_COLUMNS}
                rows={noMatch}
                getRowKey={(r) => r.traceId}
                isLoading={gaps.isLoading}
                // The forward-only "this might just be no-data-yet" note is the
                // `<p>` above; keep the empty-cell text short so the long copy
                // isn't duplicated.
                emptyMessage="No no-match asks in range"
                stickyHeader
                // `table-fixed w-full` locks column widths to the header cells
                // (see NO_MATCH_COLUMNS widths) so the wide expanded colSpan
                // sub-row wraps inside the locked layout instead of growing the
                // table and triggering a horizontal scrollbar on expand.
                tableClassName="table-fixed w-full"
                renderExpanded={renderNoMatchExpanded}
                expandedKeys={noMatchExpanded}
                onToggleExpanded={onToggleNoMatch}
              />
            </div>
          </div>
        )}
      </TabErrorBoundary>
    </section>
  );
}
